<template>
  <div class="map-wrapper">
    <div
      ref="map"
      class="map"
    />
  </div>
</template>

<script>
import Map from 'ol/Map'
import View from 'ol/View'
import {getCenter} from 'ol/extent.js';
import ImageLayer from 'ol/layer/Image.js';
import Projection from 'ol/proj/Projection.js';
import Static from 'ol/source/ImageStatic.js';
// import { fromLonLat, transformExtent } from 'ol/proj'
import { markersToLayer, markersToSource, createMarker } from '../util/maps.js'
import Zoom from 'ol/control/Zoom'

export default {
    name: 'FlatMap',
    props: {
        imageSource: {
            type: String,
            required: true
        },
        width: {
            type: Number,
            required: true
        },
        height: {
            type: Number,
            required: true
        },
        zoom: {
            type: Number,
            default: 2.5
        },
        maxZoom: {
            type: Number,
            default: 4
        },
        minZoom: {
            type: Number,
            default: 2.5
        },
        markers: {
            type: Array,
            default: () => []
        },
        debugClick: {
            type: Boolean,
            default: false
        },
        rotation: {
            type: Number,
            default: 0
        }
    },
    data() {
        return {
            markerLayer: markersToLayer([])
        }
    },
    computed: {
        extent() {
            // Map Extent is simply the image's dimensions with
            // the origin at the bottom left
            return [0, 0, this.width, this.height]
        },
        panExtent() {
            const leftamt = 585
            const rightamt = 3970
            const topamt = 2450
            const bottomamt = 1022
            return [leftamt, bottomamt, rightamt, topamt]
        },
        projection() {
            // Return a basic pixel coordinate projection - we're not dealing with
            // Earth's (or another celetial body's) coordinate systems.
            return new Projection({
                code: 'nybg-flatmap',
                units: 'pixels',
                extent: this.extent
            })
        },
        olMarkers() {
            // Convert the markers passed as a prop to OpenLayers features
            // Additionally, attach the actual exhibit object to the OL feature
            return this.markers.map(i => {
                const m = createMarker(i, this.projection)
                m.set('exhibitdata', i.exhibitdata)
                return m
            })
        },
    },
    watch: {
        markers: {
            deep: true,
            immediate: true,
            handler() {
                if (this.markers.length) {
                    this.refreshMarkers()
                }
            }
        }
    },
    mounted() {

        this.map = new Map({
            target: this.$refs.map,
            controls: [
                new Zoom()
            ],
            layers: [
                new ImageLayer({
                    source: new Static({
                        url: this.imageSource,
                        projection: this.projection,
                        imageExtent: this.extent
                    })
                }),
                this.markerLayer,
            ],
            view: new View({
                projection: this.projection,
                center: getCenter(this.extent),
                extent: this.panExtent,
                zoom: this.zoom,
                rotation: this.rotation,
                minZoom: this.minZoom,
                maxZoom: this.maxZoom,
            })
        })
        this.map.on('click', (e) => {
            if (this.debugClick) {
                console.log('Flatmap click', e.coordinate)
            }
            // Enumerate any features under the mouse and emit a Vue event with the
            // first found feature
            const clicked = e.map.forEachFeatureAtPixel(e.pixel, (f) => f)
            clicked && this.$emit('feature-click', clicked)
        })
    },
    activated() {
        // Resize the map when this view is reactivated (if it's keepalive)
        this.map.updateSize()
    },
    methods: {
        refreshMarkers() {
            this.markerLayer.setSource(markersToSource(this.olMarkers))
        }
    }
}
</script>

<style lang="scss" scoped>
.map-wrapper {
    position: relative;
    @include flex-vertical;
    @include flex-fill-vertical;
}
.map {
    @include flex-fill-vertical;
}
</style>